import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Loader2, Check, ArrowLeft, Clock, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PaymentLogos from "@/components/PaymentLogos";
import PolicyContent from "@/components/PolicyContent";
import { Calendar } from "@/components/ui/calendar";
import api from "@/lib/api";
import { useBooking } from "@/context/BookingContext";
import { useClientAuth } from "@/context/ClientAuthContext";

const STEPS = ["Service", "Date & Time", "Your Details", "Payment"];

export default function BookingModal() {
  const { open, setOpen, preselect } = useBooking();
  const { client } = useClientAuth();
  const [step, setStep] = useState(0);
  const [groups, setGroups] = useState([]);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState(null);
  const [details, setDetails] = useState({ client_name: "", client_email: "", client_phone: "", notes: "" });
  const [payment, setPayment] = useState("deposit");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [closedWeekdays, setClosedWeekdays] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [specialOpenDates, setSpecialOpenDates] = useState([]);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [waitlistError, setWaitlistError] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/services").then((r) => setGroups(r.data)).catch(() => {});
      api.get("/availability/config").then((r) => {
        setClosedWeekdays(r.data.closed_weekdays || []);
        setBlockedDates(r.data.blocked_dates || []);
        setSpecialOpenDates(r.data.special_open_dates || []);
        setMinDate(r.data.min_date || null);
        setMaxDate(r.data.max_date || null);
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setStep(0); setService(null); setDate(null); setSlots(null); setTime(null);
      setError(""); setPayment("deposit");
      setWaitlistOpen(false); setWaitlistDone(false); setWaitlistError("");
      setAgreed(false); setPolicyOpen(false);
      setDetails({
        client_name: client?.name || "",
        client_email: client?.email || "",
        client_phone: client?.phone || "",
        notes: "",
      });
    }
  }, [open]);

  useEffect(() => {
    if (open && preselect && groups.length) {
      const found = groups.flatMap((g) => g.items).find((x) => x.id === preselect);
      if (found) { setService(found); setStep(1); }
    }
  }, [open, preselect, groups]);

  const fetchSlots = async (d) => {
    setLoadingSlots(true); setSlots(null); setTime(null);
    setWaitlistOpen(false); setWaitlistDone(false); setWaitlistError("");
    try {
      const { data } = await api.get(`/availability`, { params: { date: format(d, "yyyy-MM-dd"), service_id: service.id } });
      setSlots(data.slots);
    } catch { setSlots([]); } finally { setLoadingSlots(false); }
  };

  const canWaitlist = () =>
    details.client_name.trim() && /\S+@\S+\.\S+/.test(details.client_email) && details.client_phone.trim().length >= 6;

  const submitWaitlist = async () => {
    setWaitlistSubmitting(true); setWaitlistError("");
    try {
      await api.post("/waitlist", {
        name: details.client_name, email: details.client_email, phone: details.client_phone,
        service_id: service.id, preferred_date: format(date, "yyyy-MM-dd"),
      });
      setWaitlistDone(true);
    } catch (e) {
      setWaitlistError(e.response?.data?.detail || "Could not join the waitlist. Please try again.");
    } finally { setWaitlistSubmitting(false); }
  };

  const prices = useMemo(() => {
    if (!service) return null;
    const full = service.price;
    const deposit = Math.round(full * 0.5 * 100) / 100;
    return { full, deposit, remaining: Math.round((full - deposit) * 100) / 100 };
  }, [service]);

  const disabledDays = useMemo(() => {
    const blocked = new Set(blockedDates);
    const openDates = new Set(specialOpenDates);
    const closed = new Set(closedWeekdays);
    const minD = minDate ? new Date(minDate + "T00:00:00") : null;
    const maxD = maxDate ? new Date(maxDate + "T00:00:00") : null;
    return (day) => {
      const d0 = new Date(day); d0.setHours(0, 0, 0, 0);
      if (minD && d0 < minD) return true;
      if (maxD && d0 > maxD) return true;
      const ds = format(day, "yyyy-MM-dd");
      if (blocked.has(ds)) return true;
      if (openDates.has(ds)) return false;
      return closed.has(day.getDay());
    };
  }, [closedWeekdays, blockedDates, specialOpenDates, minDate, maxDate]);

  const slotGroups = useMemo(() => {
    const g = { Morning: [], Afternoon: [], Evening: [] };
    (slots || []).forEach((s) => {
      const h = parseInt(s.time.split(":")[0], 10);
      (h < 12 ? g.Morning : h < 17 ? g.Afternoon : g.Evening).push(s);
    });
    return g;
  }, [slots]);

  const canNext = () => {
    if (step === 0) return !!service;
    if (step === 1) return !!date && !!time;
    if (step === 2)
      return details.client_name.trim() && /\S+@\S+\.\S+/.test(details.client_email) && details.client_phone.trim().length >= 6;
    return true;
  };

  const handleConfirm = async () => {
    if (!agreed) { setError("Please agree to the Booking Policy to continue."); return; }
    const embedded = window.self !== window.top;
    // Inside the embedded editor iframe, pre-open a tab on the user's click so we can send
    // them to Stripe without navigating the sandboxed frame (which the browser blocks).
    const payWin = embedded ? window.open("about:blank", "_blank") : null;
    setSubmitting(true); setError("");
    try {
      const { data: booking } = await api.post("/bookings", {
        service_id: service.id, date: format(date, "yyyy-MM-dd"), time, ...details, payment_option: payment,
      });
      const { data: checkout } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
      const url = checkout.checkout_url;
      if (payWin) {
        try { payWin.location.href = url; }
        catch {
          try { payWin.close(); } catch { /* ignore */ }
          try { if (window.top) window.top.location.href = url; else window.location.href = url; }
          catch { window.location.href = url; }
        }
        setSubmitting(false);
      } else {
        try {
          if (embedded && window.top) window.top.location.href = url;
          else window.location.href = url;
        } catch {
          const w = window.open(url, "_blank");
          if (!w) window.location.href = url;
        }
      }
    } catch (e) {
      if (payWin) payWin.close();
      setError(e.response?.data?.detail || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const labelFor = (item) => (item.type === "Single" ? item.group : `${item.group} — ${item.type}`);

  const optCls = (active) =>
    `w-full text-left border rounded-2xl p-4 transition-all duration-200 active:scale-[0.99] ${
      active
        ? "border-[#C5B382] bg-[#F8F4EA] shadow-[0_8px_20px_-6px_rgba(197,179,130,0.35)] ring-1 ring-[#C5B382]"
        : "border-[#EFECE6] bg-white hover:border-[#C5B382]/60 hover:-translate-y-0.5 hover:shadow-[0_10px_20px_-8px_rgba(26,25,24,0.1)]"
    }`;

  return (
    <>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="booking-modal"
        className="max-w-2xl w-[calc(100%-24px)] p-0 gap-0 bg-[#FAF8F5] border border-[#E5E1D8]/80 text-[#1C1917] max-h-[90vh] overflow-hidden flex flex-col rounded-2xl sm:rounded-3xl shadow-2xl"
      >
        <DialogTitle className="sr-only">Book Your Appointment</DialogTitle>
        <DialogDescription className="sr-only">Choose your service, date and time, enter your details and pay securely.</DialogDescription>

        <div className="px-5 sm:px-7 pt-6 pb-4 border-b border-[#EFECE6] bg-white/80 backdrop-blur-md">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow text-[#C5B382]">Book Your Appointment</div>
            <span className="text-[11px] font-medium text-[#1C1917]/40">Step {step + 1} of {STEPS.length}</span>
          </div>
          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            {STEPS.map((s, i) => (
              <div key={s} className="flex flex-col gap-1.5">
                <div className={`h-1.5 rounded-full transition-all duration-300 ${
                  i < step ? "bg-[#1A1918]"
                  : i === step ? "bg-[#C5B382] shadow-[0_0_8px_rgba(197,179,130,0.55)]"
                  : "bg-stone-200"}`} />
                <span className={`text-[10px] uppercase tracking-wider hidden sm:block ${i === step ? "text-[#1C1917]" : "text-[#1C1917]/35"}`}>{s}</span>
              </div>
            ))}
          </div>
        </div>

        <div key={step} className="overflow-y-auto px-5 sm:px-7 py-6 flex-1 animate-in fade-in-0 slide-in-from-right-3 duration-300">
          {step === 0 && (
            <div data-testid="step-service" className="space-y-6">
              <h3 className="font-serif text-3xl font-medium">Choose your service</h3>
              {groups.map((g) => (
                <div key={g.group}>
                  <div className="eyebrow text-[#1C1917]/45 mb-2">{g.group}</div>
                  <div className="space-y-2">
                    {g.items.map((item) => (
                      <button key={item.id} data-testid={`booking-service-${item.id}`} onClick={() => setService(item)} className={optCls(service?.id === item.id)}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{item.type === "Single" ? "Book" : item.type}</span>
                          <span className="text-[#C5B382] font-serif text-xl font-medium">${item.price.toFixed(0)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-[#1C1917]/45 mt-1"><Clock size={12} /> {item.duration} min</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-datetime" className="space-y-5">
              <h3 className="font-serif text-3xl font-medium">Choose date &amp; time</h3>
              <div className="flex justify-center bg-[#FAF8F5] border border-[#1C1917]/10 rounded-2xl p-2">
                <Calendar mode="single" selected={date} onSelect={(d) => { if (d) { setDate(d); fetchSlots(d); } }} disabled={disabledDays} />
              </div>
              {date && (
                <div>
                  <div className="eyebrow text-[#1C1917]/45 mb-3">Available times — {format(date, "EEE, d MMM")}</div>
                  {loadingSlots ? (
                    <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#C5B382]" /></div>
                  ) : slots && slots.length ? (
                    <div className="space-y-4">
                      {["Morning", "Afternoon", "Evening"].map((part) => (
                        slotGroups[part].length ? (
                          <div key={part}>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#1C1917]/40 mb-2">{part}</div>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                              {slotGroups[part].map((s) => (
                                <button key={s.time} data-testid={`slot-${s.time}`} disabled={!s.available} onClick={() => setTime(s.time)}
                                  className={`py-2.5 text-sm border rounded-lg transition-all duration-200 active:scale-[0.97] ${
                                    time === s.time ? "border-[#1A1918] bg-[#1A1918] text-white font-semibold shadow-md"
                                    : s.available ? "border-[#E5E1D8] bg-white hover:border-[#C5B382] hover:bg-[#F4EFE4] text-[#1C1917]"
                                    : "border-transparent bg-[#F5F3F0] text-[#B8B3AB] line-through cursor-not-allowed"}`}>
                                  {s.time}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null
                      ))}
                    </div>
                  ) : (
                    <p data-testid="no-slots" className="text-[#1C1917]/50 text-sm py-4">No availability on this day. Please choose another date.</p>
                  )}

                  {!loadingSlots && slots && !slots.some((s) => s.available) && (
                    <div data-testid="waitlist-section" className="mt-5 border-t border-[#1C1917]/8 pt-5">
                      {waitlistDone ? (
                        <p data-testid="waitlist-done" className="text-sm text-center text-[#1C1917]/70">
                          You're on the waitlist for <strong>{format(date, "EEE, d MMM")}</strong>. We'll be in touch if a spot opens up. 💛
                        </p>
                      ) : !waitlistOpen ? (
                        <div className="text-center">
                          <p className="text-sm text-[#1C1917]/55 mb-3">Fully booked on this day.</p>
                          <button data-testid="join-waitlist-btn" onClick={() => setWaitlistOpen(true)}
                            className="border border-[#C5B382] text-[#C5B382] hover:bg-[#C5B382] hover:text-[#1C1917] text-[11px] font-semibold tracking-[0.18em] uppercase px-6 py-3 rounded-full transition-colors">
                            Join the waitlist for this day
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-[#1C1917]/60">Leave your details and we'll contact you if <strong>{format(date, "EEE, d MMM")}</strong> frees up.</p>
                          {[
                            { key: "client_name", ph: "Full name", type: "text" },
                            { key: "client_email", ph: "Email", type: "email" },
                            { key: "client_phone", ph: "Phone number", type: "tel" },
                          ].map((f) => (
                            <input key={f.key} data-testid={`waitlist-${f.key}`} type={f.type} value={details[f.key]} placeholder={f.ph}
                              onChange={(e) => setDetails({ ...details, [f.key]: e.target.value })}
                              className="w-full bg-[#FAF8F5] border border-[#1C1917]/15 rounded-xl focus:border-[#C5B382] outline-none px-4 py-3 text-sm transition-colors" />
                          ))}
                          {waitlistError && <p data-testid="waitlist-error" className="text-red-500 text-sm">{waitlistError}</p>}
                          <div className="flex items-center gap-3">
                            <button onClick={() => setWaitlistOpen(false)} className="text-[#1C1917]/50 hover:text-[#1C1917] text-sm">Back</button>
                            <button data-testid="waitlist-submit" disabled={!canWaitlist() || waitlistSubmitting} onClick={submitWaitlist}
                              className="ml-auto bg-[#C5B382] hover:bg-[#B8A572] disabled:opacity-30 text-[#1C1917] text-[11px] font-semibold tracking-[0.15em] uppercase px-6 py-3 rounded-full transition-colors">
                              {waitlistSubmitting ? "…" : "Join Waitlist"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-details" className="space-y-4">
              <h3 className="font-serif text-3xl font-medium">Your details</h3>
              {client && (
                <div data-testid="prefill-badge" className="flex items-center gap-2 text-xs text-[#1C1917]/60 bg-[#F8F4EA] border border-[#C5B382]/30 rounded-full px-3 py-1.5 w-fit">
                  <Check size={13} className="text-[#C5B382]" /> Pre-filled from your account
                </div>
              )}
              {[
                { key: "client_name", label: "Full name", type: "text", ph: "Jane Doe" },
                { key: "client_email", label: "Email", type: "email", ph: "jane@email.com" },
                { key: "client_phone", label: "Phone number", type: "tel", ph: "04XX XXX XXX" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="eyebrow text-[#1C1917]/45 block mb-2">{f.label}</label>
                  <input data-testid={`input-${f.key}`} type={f.type} value={details[f.key]} placeholder={f.ph}
                    onChange={(e) => setDetails({ ...details, [f.key]: e.target.value })}
                    className="w-full bg-[#FAF8F5] border border-[#1C1917]/15 rounded-xl focus:border-[#C5B382] outline-none px-4 py-3 text-sm transition-colors" />
                </div>
              ))}
              <div>
                <label className="eyebrow text-[#1C1917]/45 block mb-2">Notes (optional)</label>
                <textarea data-testid="input-notes" rows={3} value={details.notes} placeholder="Anything we should know?"
                  onChange={(e) => setDetails({ ...details, notes: e.target.value })}
                  className="w-full bg-[#FAF8F5] border border-[#1C1917]/15 rounded-xl focus:border-[#C5B382] outline-none px-4 py-3 text-sm transition-colors resize-none" />
              </div>
              <p data-testid="privacy-note" className="text-xs text-[#1C1917]/50 leading-relaxed">
                By providing your contact details, you agree that Slavic Lashes &amp; Brows may use your name, phone number and email address to manage your appointment and send booking confirmations, reminders and appointment-related updates. Your information will be kept secure and will not be sold or used for unrelated purposes.
              </p>
            </div>
          )}

          {step === 3 && prices && (
            <div data-testid="step-payment" className="space-y-4">
              <h3 className="font-serif text-3xl font-medium">Choose payment</h3>
              <div className="border border-[#1C1917]/10 rounded-2xl p-4 bg-[#FAF8F5]">
                <div className="flex justify-between text-sm">
                  <span className="text-[#1C1917]/60">{labelFor(service)}</span>
                  <span className="font-serif text-xl font-medium">${prices.full.toFixed(0)}</span>
                </div>
                <div className="text-xs text-[#1C1917]/45 mt-1">{format(date, "EEE, d MMM yyyy")} · {time}</div>
              </div>

              <button data-testid="pay-deposit" onClick={() => setPayment("deposit")} className={optCls(payment === "deposit")}>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Pay 50% Deposit</span>
                  <span className="font-serif text-xl font-medium text-[#C5B382]">${prices.deposit.toFixed(2)}</span>
                </div>
                <p className="text-xs text-[#1C1917]/55 mt-1">${prices.deposit.toFixed(2)} today · ${prices.remaining.toFixed(2)} at your appointment</p>
              </button>

              <button data-testid="pay-full" onClick={() => setPayment("full")} className={optCls(payment === "full")}>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Pay in Full</span>
                  <span className="font-serif text-xl font-medium text-[#C5B382]">${prices.full.toFixed(2)}</span>
                </div>
                <p className="text-xs text-[#1C1917]/55 mt-1">Nothing left to pay at your appointment.</p>
              </button>

              <div className="flex flex-col items-center gap-2 pt-1">
                <PaymentLogos />
                <span className="text-[10px] text-[#1C1917]/45 tracking-wide text-center">Secure checkout · card details are never stored on this site</span>
              </div>

              <button
                type="button"
                data-testid="policy-agree"
                aria-pressed={agreed}
                onClick={() => { setAgreed((v) => !v); setError(""); }}
                className={`w-full flex items-start gap-3 text-left border rounded-2xl p-4 transition-colors ${
                  agreed ? "border-[#C5B382] bg-[#F8F4EA]" : "border-[#1C1917]/12 bg-white hover:border-[#C5B382]/60"
                }`}
              >
                <span className={`mt-0.5 h-5 w-5 shrink-0 rounded-md border flex items-center justify-center transition-colors ${agreed ? "bg-[#C5B382] border-[#C5B382]" : "border-[#1C1917]/25 bg-white"}`}>
                  {agreed && <Check size={13} className="text-white" />}
                </span>
                <span className="text-xs text-[#1C1917]/70 leading-relaxed">
                  I have read and agree to the{" "}
                  <span
                    data-testid="open-policy-link"
                    role="link"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setPolicyOpen(true); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setPolicyOpen(true); } }}
                    className="inline-flex items-center gap-1 text-[#C5B382] font-semibold underline underline-offset-2 hover:text-[#B3A06F]"
                  >
                    <FileText size={12} /> Booking Policy
                  </span>
                  , including the non-refundable retainer and 48-hour cancellation terms.
                </span>
              </button>

              {error && <p data-testid="booking-error" className="text-red-500 text-sm">{error}</p>}

              <p data-testid="privacy-consent" className="text-xs text-[#1C1917]/50 leading-relaxed text-center">
                By booking an appointment, you agree to our{" "}
                <span
                  data-testid="open-policy-consent"
                  role="link"
                  tabIndex={0}
                  onClick={() => setPolicyOpen(true)}
                  onKeyDown={(e) => { if (e.key === "Enter") setPolicyOpen(true); }}
                  className="text-[#C5B382] font-semibold underline underline-offset-2 hover:text-[#B3A06F] cursor-pointer"
                >
                  Privacy Policy and Booking Terms &amp; Conditions
                </span>.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#1C1917]/8 flex items-center justify-between gap-3">
          {step > 0 ? (
            <button data-testid="booking-back" onClick={() => setStep(step - 1)} className="flex items-center gap-1.5 text-[#1C1917]/50 hover:text-[#1C1917] text-sm transition-colors">
              <ArrowLeft size={16} /> Back
            </button>
          ) : <span />}

          {step < 3 ? (
            <button data-testid="booking-next" disabled={!canNext()} onClick={() => setStep(step + 1)}
              className="bg-[#C5B382] hover:bg-[#B8A572] disabled:opacity-30 disabled:cursor-not-allowed text-[#1C1917] text-[12px] font-semibold tracking-[0.15em] uppercase px-8 py-3 rounded-full transition-colors">
              Continue
            </button>
          ) : (
            <button data-testid="booking-confirm" disabled={submitting || !agreed} onClick={handleConfirm}
              className="flex items-center gap-2 bg-[#C5B382] hover:bg-[#B8A572] disabled:opacity-50 disabled:cursor-not-allowed text-[#1C1917] text-[12px] font-semibold tracking-[0.15em] uppercase px-8 py-3 rounded-full transition-colors">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Pay &amp; Confirm
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={policyOpen} onOpenChange={setPolicyOpen}>
      <DialogContent data-testid="policy-dialog" className="max-w-lg w-[calc(100%-24px)] max-h-[85vh] overflow-y-auto bg-white text-[#1C1917] rounded-[1.5rem]">
        <DialogTitle className="font-serif text-3xl font-medium">Booking Policy</DialogTitle>
        <DialogDescription className="sr-only">Our booking, cancellation, late arrival and health &amp; safety policies.</DialogDescription>
        <PolicyContent className="mt-1" />
      </DialogContent>
    </Dialog>
    </>
  );
}
